import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import * as api from '../services/ClusteringLabService';
import { ClusteringLabDuplicateSet } from '../types';

const ZOOM_DELAY_MS = 3000;
const ZOOM_SIZE_PX = 480;

interface DuplicatesPanelProps {
  userLid: number;
}

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f7fafc;
`;

const Hint = styled.div`
  max-width: 720px;
  margin: 0 auto 16px;
  font-size: 13px;
  color: #718096;
  text-align: center;
`;

const SetCard = styled.div`
  max-width: 1200px;
  margin: 0 auto 12px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 14px 16px;
`;

const SetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 11px;
  color: #a0aec0;
`;

const SetCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PhotoStrip = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  min-width: 0;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
  }
`;

const PhotoTile = styled.div<{ $selected: boolean }>`
  flex-shrink: 0;
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${(p) => (p.$selected ? '#3182ce' : 'transparent')};
  box-sizing: border-box;
  transition: border-color 0.1s;

  &:hover {
    border-color: ${(p) => (p.$selected ? '#3182ce' : '#cbd5e0')};
  }
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Conf = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  pointer-events: none;
`;

const ZoomPreview = styled.div<{ $left: number; $top: number }>`
  position: fixed;
  left: ${(p) => p.$left}px;
  top: ${(p) => p.$top}px;
  z-index: 1000;
  width: ${ZOOM_SIZE_PX}px;
  height: ${ZOOM_SIZE_PX}px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #111;
  }
`;

const ActionArea = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ConfirmBtn = styled.button`
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.1s;

  &:hover {
    background: #edf2f7;
    color: #2d3748;
  }
`;

const RejectBtn = styled.button`
  background: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.1s;

  &:hover {
    background: #fc8181;
    color: white;
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #a0aec0;
  font-size: 14px;
`;

const LoadMore = styled.button`
  display: block;
  margin: 16px auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f7fafc;
  }
`;

interface SetState extends ClusteringLabDuplicateSet {
  selected: Set<string>;
}

interface ZoomState {
  url: string;
  left: number;
  top: number;
}

const DuplicatesPanel: React.FC<DuplicatesPanelProps> = ({ userLid }) => {
  const [sets, setSets] = useState<SetState[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const zoomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverStart = useCallback((url: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      const margin = 12;
      const wantRight = rect.right + margin;
      const fitsRight = wantRight + ZOOM_SIZE_PX <= window.innerWidth;
      const left = fitsRight ? wantRight : Math.max(margin, rect.left - ZOOM_SIZE_PX - margin);
      const top = Math.max(margin, Math.min(window.innerHeight - ZOOM_SIZE_PX - margin, rect.top));
      setZoom({ url, left, top });
    }, ZOOM_DELAY_MS);
  }, []);

  const handleHoverEnd = useCallback(() => {
    if (zoomTimerRef.current) {
      clearTimeout(zoomTimerRef.current);
      zoomTimerRef.current = null;
    }
    setZoom(null);
  }, []);

  useEffect(() => {
    return () => {
      if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    };
  }, []);

  const load = useCallback(
    async (nextCursor: string | null) => {
      setLoading(true);
      try {
        const res = await api.listDuplicateSets(userLid, nextCursor);
        if (!res) return;
        const newSets: SetState[] = res.sets.map((s) => ({ ...s, selected: new Set<string>() }));
        setSets((prev) => (nextCursor ? [...prev, ...newSets] : newSets));
        setCursor(res.next_cursor ?? null);
        setHasMore(!!res.next_cursor);
      } finally {
        setLoading(false);
      }
    },
    [userLid]
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const togglePhoto = useCallback((setIdx: number, photoId: string) => {
    setSets((prev) =>
      prev.map((s, i) => {
        if (i !== setIdx) return s;
        const next = new Set(s.selected);
        if (next.has(photoId)) next.delete(photoId);
        else next.add(photoId);
        return { ...s, selected: next };
      })
    );
  }, []);

  const handleNotDuplicate = useCallback(
    async (setIdx: number) => {
      const s = sets[setIdx];
      if (!s || s.selected.size === 0 || s.selected.size === s.photos.length) return;
      const allIds = s.photos.map((p) => p.photo_id);
      const selectedIds = Array.from(s.selected);
      await api.markNotDuplicate(userLid, allIds, selectedIds);
      setSets((prev) => prev.filter((_, i) => i !== setIdx));
    },
    [sets, userLid]
  );

  const handleConfirm = useCallback(
    async (setIdx: number) => {
      const s = sets[setIdx];
      if (!s) return;
      const ids = s.photos.map((p) => p.photo_id);
      await api.confirmDuplicates(userLid, ids);
      setSets((prev) => prev.filter((_, i) => i !== setIdx));
    },
    [sets, userLid]
  );

  if (loading && sets.length === 0) {
    return (
      <Container>
        <Empty>Loading duplicate sets...</Empty>
      </Container>
    );
  }

  if (sets.length === 0) {
    return (
      <Container>
        <Empty>No duplicate sets to review.</Empty>
      </Container>
    );
  }

  return (
    <Container>
      <Hint>Select photos that don't belong, or click "Looks right" to confirm the whole set.</Hint>
      {sets.map((s, idx) => {
        const conf = s.min_confidence != null ? s.min_confidence.toFixed(2) : null;
        const selectedCount = s.selected.size;
        const hasPartialSelection = selectedCount > 0 && selectedCount < s.photos.length;

        return (
          <SetCard key={idx}>
            <SetHeader>
              <SetCount>{s.photos.length} photos</SetCount>
              {conf && <span>min sim {conf}</span>}
            </SetHeader>
            <Row>
              <PhotoStrip>
                {s.photos.map((p) => {
                  const isSelected = s.selected.has(p.photo_id);
                  return (
                    <PhotoTile
                      key={p.photo_id}
                      $selected={isSelected}
                      onClick={() => togglePhoto(idx, p.photo_id)}
                      onMouseEnter={(e) => handleHoverStart(p.photo_url, e)}
                      onMouseLeave={handleHoverEnd}
                    >
                      <Img src={p.photo_url} loading="lazy" />
                      {p.confidence != null && <Conf>{p.confidence.toFixed(2)}</Conf>}
                    </PhotoTile>
                  );
                })}
              </PhotoStrip>
              <ActionArea>
                {hasPartialSelection ? (
                  <RejectBtn onClick={() => handleNotDuplicate(idx)}>
                    {selectedCount} not duplicate{selectedCount > 1 ? 's' : ''}
                  </RejectBtn>
                ) : (
                  <ConfirmBtn onClick={() => handleConfirm(idx)}>Looks right</ConfirmBtn>
                )}
              </ActionArea>
            </Row>
          </SetCard>
        );
      })}
      {hasMore && (
        <LoadMore onClick={() => load(cursor)} disabled={loading}>
          {loading ? 'Loading...' : 'Load More'}
        </LoadMore>
      )}
      {zoom && (
        <ZoomPreview $left={zoom.left} $top={zoom.top}>
          <img src={zoom.url} alt="" />
        </ZoomPreview>
      )}
    </Container>
  );
};

export default DuplicatesPanel;
